<template>
  <DialogFull
    :data="dialogSettings"
    progress
    @close="close"
    @onStepBack="stepBack"
    :steps="{ current: step, total: totalSteps }"
  >
    <template v-slot:dialog.body>
      <div class="container col-lg-8 col-sm-12">
        <SelectInterviewFormat
          ref="step1"
          v-if="shouldStepViewBeShown(1) || ifStepHasAlreadyBeenCompleted(1)"
          v-show="shouldStepViewBeShown(1)"
          @onChange="updateData"
        />
        <SelectCandidates
          ref="step2"
          v-if="shouldStepViewBeShown(2) || ifStepHasAlreadyBeenCompleted(2)"
          v-show="shouldStepViewBeShown(2)"
          @onChange="updateData"
          :interviewFormatId="inviteData.interviewFormat.id"
        />
        <SelfScheduleSlots
          ref="step3"
          v-if="shouldStepViewBeShown(3) || ifStepHasAlreadyBeenCompleted(3)"
          v-show="shouldStepViewBeShown(3)"
          :interviewFormat="inviteData.interviewFormat"
          @onSlotsDataChange="updateData"
          @onRemoveSlot="handleRemoveSlot"
        />
        <ConfirmDetails
          ref="step4"
          v-if="shouldStepViewBeShown(4) || ifStepHasAlreadyBeenCompleted(4)"
          v-show="shouldStepViewBeShown(4)"
          :inviteData="inviteData"
          :interviewFormat="inviteData.interviewFormat"
          :allSlots="allSlots"
        />
      </div>
    </template>
    <template v-slot:dialog.action>
      <PrimaryButton
        @click.native="validateStep"
        :loading="isLoading"
        :disabled="!showButton"
      >
        {{ buttonText }}
      </PrimaryButton>
    </template>
  </DialogFull>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import SelectCandidates from "@/views/bookings/BookingInterviews/components/dialogs/SelfScheduleInvites/SelfScheduleInvite/steps/SelectCandidates";
import SelectInterviewFormat from "@/views/bookings/BookingInterviews/components/dialogs/SelfScheduleInvites/SelfScheduleInvite/steps/SelectInterviewFormat";
import ConfirmDetails from "@/views/bookings/BookingInterviews/components/dialogs/SelfScheduleInvites/SelfScheduleInvite/steps/ConfirmDetails";
import SelfScheduleSlots from "@/views/bookings/BookingInterviews/components/dialogs/SelfScheduleInvites/SelfScheduleInvite/steps/SelfScheduleSlots/SelfScheduleSlots";
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import { createNamespacedHelpers } from "vuex";
import {
  CREATE_SELF_SCHEDULE_SLOTS,
  EDIT_SELF_SCHEDULE_SLOTS,
  ADD_SELF_SCHEDULE_INVITE
} from "@/store/modules/bookings/interviews/actions";
import { map, isEmpty, size, filter, find } from "lodash";

const MAX_STEPS = 4;
const SELF_SCHEDULE_SLOTS_STEP_NUMBER = 3;

const { mapActions: mapInterviewActions } = createNamespacedHelpers(
  INTERVIEWS_NAMESPACE
);

export default {
  name: "SelfScheduleInviteDialog",
  components: {
    PrimaryButton,
    DialogFull,
    SelectCandidates,
    SelectInterviewFormat,
    SelfScheduleSlots,
    ConfirmDetails
  },
  data() {
    return {
      totalSteps: MAX_STEPS,
      step: 1,
      savedStep: 1,
      inviteData: {},
      isLoading: false,
      slotsToDelete: []
    };
  },
  computed: {
    dialogSettings() {
      return {
        dialog: this.isOpen,
        title: "Candidate Self-Schedule Interview"
      };
    },
    buttonText() {
      return this.step < this.totalSteps ? "Continue" : "Send Self-Schedule(s)";
    },
    currentBookingId() {
      return this.$route.params.id;
    },
    allSlots() {
      return filter(
        this.allAvailableSlots,
        ({ id }) => !find(this.slotsToDelete, { id })
      );
    },
    allAvailableSlots() {
      const { interviewSlots, existingSlots } = this.inviteData;
      return [...(interviewSlots || []), ...(existingSlots || [])];
    },
    showButton() {
      if (this.step === 3) {
        return size(this.allAvailableSlots);
      }
      return true;
    }
  },
  methods: {
    ...mapInterviewActions({
      createSelfScheduleSlots: CREATE_SELF_SCHEDULE_SLOTS,
      addSelfScheduleInvite: ADD_SELF_SCHEDULE_INVITE,
      editSelfScheduleSlots: EDIT_SELF_SCHEDULE_SLOTS
    }),
    updateData(data) {
      this.inviteData = { ...this.inviteData, ...data };
    },
    handleRemoveSlot({ id }) {
      if (!find(this.slotsToDelete, { id })) {
        this.slotsToDelete.push({ id });
      }
    },
    stepBack() {
      if (this.step > 1) {
        this.step -= 1;
      }
    },
    async validateStep() {
      if (this.step === this.totalSteps) {
        this.handleSubmit();
      } else {
        const isStepValid =
          this.step === SELF_SCHEDULE_SLOTS_STEP_NUMBER ||
          (await this.$refs[`step${this.step}`].validateForm());
        if (isStepValid) {
          this.stepForward();
        }
      }
    },
    stepForward() {
      this.step += 1;
      if (this.savedStep < this.totalSteps && this.step > this.savedStep) {
        this.savedStep += 1;
      }
    },
    async onCreateSelfScheduleSlots() {
      const {
        interviewSlots,
        interviewers,
        candidates,
        interviewFormat,
        location
      } = this.inviteData;
      const formattedSlots = map(interviewSlots, ({ startTime, endTime }) => ({
        startTime,
        endTime
      }));
      const formattedInterviewers = map(interviewers, ({ id }) => ({ id }));
      const formattedCandidates = map(candidates, ({ id }) => ({ id }));
      await this.createSelfScheduleSlots({
        data: {
          slotsToAdd: formattedSlots,
          candidates: formattedCandidates,
          interviewers: formattedInterviewers,
          location
        },
        interviewFormatId: interviewFormat.id,
        bookingId: this.currentBookingId
      });
    },
    async createInterviewInvites() {
      const requests = map(this.inviteData.candidates, ({ id }) =>
        this.addSelfScheduleInvite({
          data: {
            userId: id
          },
          interviewFormatId: this.inviteData.interviewFormat.id,
          bookingId: this.currentBookingId
        })
      );
      return Promise.all(requests);
    },
    async handleSubmit() {
      const shouldCreateInterviewSlots = size(this.inviteData.interviewSlots);
      this.isLoading = true;
      if (shouldCreateInterviewSlots) {
        await this.onCreateSelfScheduleSlots();
      } else if (size(this.allSlots)) {
        await this.createInterviewInvites();
      }
      if (size(this.slotsToDelete)) {
        await this.editSelfScheduleSlots({
          data: {
            slotsToDelete: this.slotsToDelete
          },
          interviewFormatId: this.inviteData.interviewFormat.id
        });
      }
      this.close();
      this.isLoading = false;
    },
    shouldStepViewBeShown(viewStep) {
      return viewStep <= this.totalSteps && this.step === viewStep;
    },
    ifStepHasAlreadyBeenCompleted(viewStep) {
      return viewStep <= this.savedStep;
    },
    close() {
      this.$emit("close");
    },
    isEmpty
  },
  props: {
    isOpen: Boolean
  }
};
</script>

<style lang="scss"></style>
